import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import api from '@/lib/api';
import { Eye, EyeOff, Lock, CheckCircle2, XCircle, Loader2, ArrowLeft } from 'lucide-react';

/**
 * ResetPassword
 * -------------
 * Consumes a single-use token from the URL query (?token=...). On mount we
 * pre-validate the token via GET /api/auth/verify-reset-token/{token} so the
 * user sees a clear error before bothering to type a new password.
 *
 * On success: backend sets the new password_hash and marks the token used.
 * We then redirect to /login with a toast.
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = useMemo(() => params.get('token') || '', [params]);

    const [tokenStatus, setTokenStatus] = useState('checking'); // checking | valid | invalid
    const [tokenEmail, setTokenEmail] = useState('');
    const [tokenError, setTokenError] = useState('');

    const [pw1, setPw1] = useState('');
    const [pw2, setPw2] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Validate token on mount (and any time the URL changes)
    useEffect(() => {
        let cancelled = false;
        async function check() {
            if (!token) {
                setTokenStatus('invalid');
                setTokenError('No reset token in the link. Request a new one.');
                return;
            }
            try {
                const r = await api.get(`/auth/verify-reset-token/${encodeURIComponent(token)}`);
                if (cancelled) return;
                setTokenEmail(r.data?.email || '');
                setTokenStatus('valid');
            } catch (err) {
                if (cancelled) return;
                setTokenStatus('invalid');
                const detail = err?.response?.data?.detail;
                setTokenError(detail || 'Reset link is invalid or has expired.');
            }
        }
        check();
        return () => { cancelled = true; };
    }, [token]);

    // ── Password strength tiny checker ───────────────────────────────────────
    const pwIssues = useMemo(() => {
        const issues = [];
        if (pw1.length > 0 && pw1.length < 8) issues.push('At least 8 characters');
        if (pw2.length > 0 && pw1 !== pw2) issues.push('Passwords must match');
        return issues;
    }, [pw1, pw2]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw1.length < 8) {
            toast.error('Password must be at least 8 characters.');
            return;
        }
        if (pw1 !== pw2) {
            toast.error('Passwords don\u2019t match.');
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post('/auth/reset-password', { token, new_password: pw1 });
            toast.success(r.data?.message || 'Password updated. Please sign in.');
            // Small delay so the user sees the toast before redirect
            setTimeout(() => navigate('/login', { replace: true }), 400);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 410) {
                setTokenStatus('invalid');
                setTokenError(detail || 'Reset link has expired.');
            } else if (status === 429) {
                toast.error('Too many attempts. Wait a bit and try again.');
            } else if (detail) {
                toast.error(detail);
            } else {
                toast.error('Couldn\u2019t reset password. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-amber-50 flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 bg-white/60 backdrop-blur">
                <Link to="/" className="inline-flex items-center" data-testid="reset-home-link"><Logo /></Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Card className="border-slate-200 shadow-lg">
                        <CardHeader>
                            <CardTitle className="font-display text-2xl text-slate-900">
                                {tokenStatus === 'invalid' ? 'Link expired' : 'Choose a new password'}
                            </CardTitle>
                            {tokenStatus === 'valid' && tokenEmail && (
                                <p className="text-sm text-slate-600">
                                    For account <b>{tokenEmail}</b>. Pick a strong password &mdash; minimum 8 characters.
                                </p>
                            )}
                        </CardHeader>

                        <CardContent>
                            {tokenStatus === 'checking' && (
                                <div className="flex items-center gap-2 text-slate-500 text-sm py-6" data-testid="reset-checking">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Verifying your reset link\u2026
                                </div>
                            )}

                            {tokenStatus === 'invalid' && (
                                <div className="space-y-4" data-testid="reset-invalid-card">
                                    <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
                                        <XCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                                        <div className="text-sm text-rose-900">{tokenError}</div>
                                    </div>
                                    <Button asChild className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="reset-request-new-link">
                                        <Link to="/forgot-password">Request a new link</Link>
                                    </Button>
                                    <Button asChild variant="outline" className="w-full" data-testid="reset-back-to-login">
                                        <Link to="/login">
                                            <ArrowLeft className="mr-2 h-4 w-4" /> Back to sign in
                                        </Link>
                                    </Button>
                                </div>
                            )}

                            {tokenStatus === 'valid' && (
                                <form onSubmit={submit} className="space-y-4">
                                    <div>
                                        <Label htmlFor="pw1">New password</Label>
                                        <div className="relative">
                                            <Input
                                                id="pw1"
                                                type={showPw ? 'text' : 'password'}
                                                required
                                                minLength={8}
                                                placeholder="At least 8 characters"
                                                value={pw1}
                                                onChange={(e) => setPw1(e.target.value)}
                                                autoComplete="new-password"
                                                autoFocus
                                                data-testid="reset-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPw(s => !s)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                                aria-label={showPw ? 'Hide password' : 'Show password'}
                                                data-testid="reset-toggle-show-pw"
                                            >
                                                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <Label htmlFor="pw2">Confirm new password</Label>
                                        <Input
                                            id="pw2"
                                            type={showPw ? 'text' : 'password'}
                                            required
                                            minLength={8}
                                            placeholder="Re-enter the same password"
                                            value={pw2}
                                            onChange={(e) => setPw2(e.target.value)}
                                            autoComplete="new-password"
                                            data-testid="reset-confirm-input"
                                        />
                                    </div>

                                    {pwIssues.length > 0 && (
                                        <ul className="text-xs text-rose-600 list-disc pl-5 space-y-0.5" data-testid="reset-pw-issues">
                                            {pwIssues.map((issue, i) => <li key={i}>{issue}</li>)}
                                        </ul>
                                    )}

                                    {pw1.length >= 8 && pw1 === pw2 && (
                                        <div className="flex items-center gap-2 text-xs text-emerald-700">
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            Looks good — passwords match.
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={submitting || pwIssues.length > 0 || pw1.length < 8 || pw1 !== pw2}
                                        className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                        data-testid="reset-submit-button"
                                    >
                                        <Lock className="mr-2 h-4 w-4" />
                                        {submitting ? 'Updating\u2026' : 'Set new password'}
                                    </Button>

                                    <p className="text-center text-sm text-slate-600">
                                        Changed your mind? <Link to="/login" className="text-slate-900 font-medium underline" data-testid="reset-to-login-link">Back to sign in</Link>
                                    </p>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
